import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { House, ArrowRight, SealCheck, ChartLineUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { PROPERTY_TYPES, formatPrice } from "@/lib/constants";
import Seo from "@/lib/seo";
import { track } from "@/lib/tracking";

const CONDITIONS = ["Renovated", "Move-in ready", "Average", "Needs work"];
const TIMELINES = ["ASAP", "1-3 months", "3-6 months", "Just curious"];

export default function HomeValue() {
  const [form, setForm] = useState({
    address: "", property_type: "Single Family", bedrooms: "3", bathrooms: "2",
    sqft: "", condition: "Move-in ready", name: "", email: "", phone: "", timeline: "3-6 months",
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.address || !form.name || !form.email) {
      toast.error("Please add the address, your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/home-value", {
        address: form.address,
        property_type: form.property_type,
        bedrooms: Number(form.bedrooms),
        bathrooms: Number(form.bathrooms),
        sqft: form.sqft ? Number(form.sqft) : null,
        condition: form.condition,
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        timeline: form.timeline,
      });
      setResult(data);
      track("home_value", {});
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fieldCls = "w-full bg-[#0B0C10] border border-white/10 rounded-sm px-3.5 py-2.5 text-sm text-white placeholder-white/30 outline-none focus:border-brand/50";
  const labelCls = "block text-[11px] uppercase tracking-wider text-white/40 mb-1.5";

  return (
    <div className="bg-background min-h-screen">
      <SiteNav />
      <div className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <div className="mb-8 text-center">
          <p className="text-brand text-xs uppercase tracking-[0.3em] mb-3">Free · Instant</p>
          <h1 className="text-4xl md:text-5xl text-white flex items-center justify-center gap-3">
            <ChartLineUp size={36} weight="duotone" className="text-brand" /> What&apos;s my home worth?
          </h1>
          <p className="text-white/50 mt-3 max-w-xl mx-auto">
            Get an instant estimated value range for your New York home, then Lamar will follow up
            with a precise, no-obligation comparative market analysis.
          </p>
        </div>

        {!result ? (
          <form onSubmit={submit} className="bg-[#101116] border border-white/5 rounded-sm p-6 sm:p-8 space-y-5" data-testid="home-value-form">
            <div>
              <label className={labelCls}>Property address</label>
              <input data-testid="hv-address" className={fieldCls} value={form.address} onChange={set("address")} placeholder="123 Main St, Bronx, NY 10466" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Property type</label>
                <select data-testid="hv-type" className={fieldCls} value={form.property_type} onChange={set("property_type")}>
                  {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Condition</label>
                <select data-testid="hv-condition" className={fieldCls} value={form.condition} onChange={set("condition")}>
                  {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Beds</label>
                <input data-testid="hv-beds" type="number" min="0" className={fieldCls} value={form.bedrooms} onChange={set("bedrooms")} />
              </div>
              <div>
                <label className={labelCls}>Baths</label>
                <input data-testid="hv-baths" type="number" min="0" step="0.5" className={fieldCls} value={form.bathrooms} onChange={set("bathrooms")} />
              </div>
              <div>
                <label className={labelCls}>Sq. ft.</label>
                <input data-testid="hv-sqft" type="number" min="0" className={fieldCls} value={form.sqft} onChange={set("sqft")} placeholder="optional" />
              </div>
            </div>

            <div className="pt-2 border-t border-white/5" />
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Your name</label>
                <input data-testid="hv-name" className={fieldCls} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input data-testid="hv-email" type="email" className={fieldCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
              </div>
              <div>
                <label className={labelCls}>Phone (optional)</label>
                <input data-testid="hv-phone" type="tel" className={fieldCls} value={form.phone} onChange={set("phone")} placeholder="+1 (555) 123-4567" />
              </div>
              <div>
                <label className={labelCls}>Timeline</label>
                <select data-testid="hv-timeline" className={fieldCls} value={form.timeline} onChange={set("timeline")}>
                  {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <button type="submit" disabled={loading} data-testid="hv-submit" className="w-full flex items-center justify-center gap-2 rounded-sm bg-brand py-3 text-sm font-medium uppercase tracking-wider text-black hover:bg-brand-hover disabled:opacity-60">
              {loading ? "Calculating…" : <>Get my estimate <ArrowRight size={16} weight="bold" /></>}
            </button>
            <p className="text-[11px] text-white/30 text-center">Estimate only — not an appraisal. A precise CMA from Lamar is free.</p>
          </form>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-[#101116] border border-brand/20 rounded-sm p-8 text-center" data-testid="home-value-result">
            <p className="text-brand text-xs uppercase tracking-[0.3em] mb-4">Estimated market value</p>
            <p className="text-4xl sm:text-5xl font-serif text-white">
              {formatPrice(result.low)} <span className="text-white/30">–</span> {formatPrice(result.high)}
            </p>
            <p className="text-white/50 text-sm mt-2">Midpoint estimate {formatPrice(result.estimate)}</p>
            {result.summary && <p className="text-white/70 mt-6 leading-relaxed max-w-xl mx-auto">{result.summary}</p>}

            <div className="mt-8 rounded-sm border border-brand/30 bg-brand/[0.06] p-5 text-left flex items-center gap-3">
              <SealCheck size={28} weight="duotone" className="shrink-0 text-brand" />
              <p className="text-sm text-white/80">
                Lamar has your details and will reach out with a precise, no-obligation CMA — the real number,
                backed by live comps.
              </p>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/book" data-testid="hv-book-call" className="rounded-sm bg-brand px-6 py-3 text-sm font-medium uppercase tracking-wider text-black hover:bg-brand-hover">Book a free strategy call</Link>
              <Link to="/sell" className="rounded-sm border border-brand/40 px-6 py-3 text-sm text-brand uppercase tracking-wider hover:bg-brand/10">Plan my sale</Link>
              <Link to="/listings" className="rounded-sm border border-white/15 px-6 py-3 text-sm text-white/70 hover:text-white">Browse homes</Link>
            </div>
            <p className="text-[11px] text-white/30 mt-6">This automated estimate is for informational purposes only and is not an appraisal or guarantee of value.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
